<template>
    <div>
        <div class="mt-2 mb-3">
            <div class="row align-items-center">
                <div class="col-9">
                <span class="input-group">
                    <span class="input-group-text input-group-icon my-2" :class="{'is-invalid': isRoomNotSelected()}">
                        <DoorIcon class="text-primary"/>
                    </span>
                    <select class="form-select custom-select my-2"
                            :value="state.roomType"
                            @input="updateRoomType($event.target.value)"
                            :class="{'is-invalid': isRoomNotSelected()}">
                        <option value="">{{ $t('form.floorArea.selectRoomType') }}</option>
                        <optgroup v-for="(list, roomGroup) in roomLists" :label="$t('floorArea.roomGroups.' + roomGroup)">
                            <option v-for="roomType in list" :value="roomType">
                                {{ $t('floorArea.roomTypes.' + roomType) }}
                            </option>
                        </optgroup>
                    </select>
                </span>
                    <div class="invalid-feedback d-block" v-if="isRoomNotSelected()">{{ $t('form.errors.roomNotSelected')}}</div>
                </div>
                <div class="col-3 pe-0 form-height">
                    <button class="btn btn-outline-secondary ms-2"
                            :aria-label="$t('form.floorArea.buttonCloneRoom')"
                            :title="$t('form.floorArea.buttonCloneRoom')"
                            @click="$emit('clone')"
                    >
                        <span class="fas fa-clone"></span>
                    </button>
                    <button @click="removeRoom"
                            class="btn btn-outline-secondary ms-2"
                    >
                        <span class="fas fa-times"></span>
                    </button>
                </div>
            </div>
        </div>

        <div>
            <div v-for="(dimensionsEntry, index) in state.dimensions" class="row">
                <div class="col-9 form-inline room-dimension">
                    <span class="input-group">
                        <input :value="dimensionsEntry[0]"
                               placeholder="X"
                               @input="updatedDimensions(index, 0, $event.target.value)"
                               class="form-control"
                               :class="{'is-invalid': ! areValid(index)}"
                        >
                        <span class="input-group-text rounded-end">m</span>
                        <span class="px-3 py-2">&times;</span>
                        <input :value="dimensionsEntry[1]"
                               placeholder="Y"
                               @input="updatedDimensions(index, 1, $event.target.value)"
                               class="form-control rounded-start"
                               :class="{'is-invalid': ! areValid(index)}"
                        >
                        <span class="input-group-text rounded-end">m</span>
                        <span class="px-3 py-2">=</span>
                        <input type="text"
                               @input="updatedDimensions(index, 2, $event.target.value)"
                               :value="dimensionsEntry[2]"
                               class="form-control rounded-start"
                               :class="{'is-invalid': ! areValid(index)}"
                        >
                        <span class="input-group-text">m<sup>2</sup></span>
                    </span>
                    <div class="invalid-feedback d-block" v-if="! areValid(index)">
                        {{ $t('form.errors.invalidRoomDimensions') }}
                    </div>
                </div>
                <div class="col-3 form-height ps-3 justify-content-center justify-content-md-start">
                    <button v-if="index > 0"
                            @click="removeDimensions(index)"
                            class="btn btn-outline-secondary ms-2"
                    >
                        <span class="fas fa-times"></span>
                    </button>
                    <button v-if="index === state.dimensions.length - 1"
                            @click="addDimensions"
                            class="btn btn-outline-secondary ms-2"
                    >
                        <span class="fas fa-plus"></span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {DoorIcon} from '@/icons';
import {RoomControlState} from '@/model/state/RoomControlState';
import {parseDecimalNumber} from '@/utils';
import {round} from 'lodash-es';
import {defineComponent, PropType} from 'vue';
import {RoomList} from './values';

type RawDimensions = [string, string, string];
type Dimensions = [number, number]|number;

export default defineComponent({
    components: {DoorIcon},
    emits: ['clone', 'remove'],
    props: {
        roomLists: {
            type: Object as PropType<{ [id: string]: RoomList }>,
            required: true,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        state: {
            type: Object as PropType<RoomControlState>,
            required: true,
        },
    },
    setup(props, {emit}) {
        function updatedDimensions(index: number, position: number, value: string): void {
            const dimensions: RawDimensions = Array.from(props.state.dimensions[index]) as RawDimensions;

            dimensions[position] = value;

            if (position === 2) {
                dimensions[0] = '';
                dimensions[1] = '';
            } else {
                const area = parseDecimalNumber(dimensions[0]) * parseDecimalNumber(dimensions[1]) || 0;
                dimensions[2] = '' + round(area, 2);
            }

            props.state.updateDimensions(index, dimensions);
        }

        function addDimensions(): void {
            props.state.addDimensions(['', '', '']);
        }

        function removeRoom(): void {
            emit('remove');
        }

        function removeDimensions(index: number): void {
            props.state.removeDimensions(index);
        }

        function areValid(index: number): boolean {
            return ! props.validate || props.state.areDimensionsValid(index);
        }

        function updateRoomType(roomType: string): void {
            props.state.updateRoomType(roomType, props.roomLists.additional.includes(roomType));
        }

        function isRoomNotSelected(): boolean {
            return props.validate && props.state.roomType === '';
        }

        return {
            updatedDimensions,
            addDimensions,
            removeRoom,
            removeDimensions,
            areValid,
            updateRoomType,
            isRoomNotSelected,
        };
    },
});
</script>
